/**
 * fetchClassification.ts — T-498, the vicinity gather behind the PDF one-pager's classification block.
 *
 * The stats panel reads a site's block/block-group classification via the useSiteVicinity hook; the
 * PDF export runs outside React, so this is the plain-async twin of that fetch (it mirrors
 * fetchNearby.ts). It hits the SAME endpoint (GET /api/vicinity) and runs the SAME pure derivation
 * (selectLocationClassification) the panel uses — so the printed classification is identical to the
 * on-screen one, never a re-derivation.
 *
 * DEGRADATION: any failure (a non-OK response, an offline/aborted request, an unparseable body)
 * returns null — the model then prints an honest "unavailable" note in the block's place, never a
 * fabricated class.
 */
import { authedFetch } from '../api'
import {
  selectLocationClassification,
  type LocationClassification,
} from '../locationClassification'
import type { VicinityResponse } from '../../hooks/useSiteVicinity'
import type { BlockInputs, BlockGroupInputs } from './onePagerModelV2'
import { pointInPolygon } from '../pointInPolygon'

/**
 * Fetch the vicinity payload for a site and derive its block + block-group classification, or null
 * on any failure. Reuses selectLocationClassification, so the PDF and the panel agree exactly.
 */
export async function fetchSiteClassification(
  lat: number,
  lng: number,
): Promise<LocationClassification | null> {
  try {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
    const res = await authedFetch(`/api/vicinity?${params}`)
    if (!res.ok) return null
    const body = (await res.json()) as VicinityResponse
    return selectLocationClassification(
      { lat, lng },
      {
        blocks: body.blocks,
        blockGroups: body.blockGroups,
      },
    )
  } catch {
    return null
  }
}

export interface ClassificationWithInputs {
  classification: LocationClassification | null
  blockInputs: BlockInputs | null
  blockGroupInputs: BlockGroupInputs | null
}

/** Find the feature whose polygon contains the point. */
function findContainingFeature(
  fc: GeoJSON.FeatureCollection,
  lat: number,
  lng: number,
): GeoJSON.Feature | null {
  for (const f of fc.features) {
    if (f.geometry && pointInPolygon(lng, lat, f.geometry)) {
      return f
    }
  }
  return null
}

/**
 * V2: fetch classification + raw block/block group demographic inputs for the PDF.
 */
export async function fetchSiteClassificationWithInputs(
  lat: number,
  lng: number,
): Promise<ClassificationWithInputs | null> {
  try {
    const params = new URLSearchParams({ lat: String(lat), lng: String(lng) })
    const res = await authedFetch(`/api/vicinity?${params}`)
    if (!res.ok) return null
    const body = (await res.json()) as VicinityResponse

    const classification = selectLocationClassification(
      { lat, lng },
      { blocks: body.blocks, blockGroups: body.blockGroups },
    )

    // Extract raw properties from the containing block and block group
    const blockFeature = findContainingFeature(body.blocks, lat, lng)
    const bgFeature = findContainingFeature(body.blockGroups, lat, lng)

    const blockInputs: BlockInputs | null = blockFeature?.properties
      ? {
          jobs_retail_pct: blockFeature.properties.jobs_retail_pct as number | null,
          jobs_food_accommodation_pct: blockFeature.properties.jobs_food_accommodation_pct as
            number | null,
          jobs_entertainment_pct: blockFeature.properties.jobs_entertainment_pct as number | null,
          jobs_healthcare_pct: blockFeature.properties.jobs_healthcare_pct as number | null,
          daytime_pop_ratio: blockFeature.properties.daytime_pop_ratio as number | null,
          population_blk: blockFeature.properties.population_blk as number | null,
          population_density_blk: blockFeature.properties.population_density_blk as number | null,
        }
      : null

    const blockGroupInputs: BlockGroupInputs | null = bgFeature?.properties
      ? {
          pct_renter_bg: bgFeature.properties.pct_renter_bg as number | null,
          multifamily_share_bg: bgFeature.properties.multifamily_share_bg as number | null,
          mean_commute_time_bg: bgFeature.properties.mean_commute_time_bg as number | null,
          sf_detached_share_bg: bgFeature.properties.sf_detached_share_bg as number | null,
          pct_owner_occupied_bg: bgFeature.properties.pct_owner_occupied_bg as number | null,
          mean_vehicles_per_hh_bg: bgFeature.properties.mean_vehicles_per_hh_bg as number | null,
          pct_drove_alone_bg: bgFeature.properties.pct_drove_alone_bg as number | null,
          median_home_value_bg: bgFeature.properties.median_home_value_bg as number | null,
          population_bg: bgFeature.properties.population_bg as number | null,
          population_density_bg: bgFeature.properties.population_density_bg as number | null,
          household_density_bg: bgFeature.properties.household_density_bg as number | null,
        }
      : null

    return { classification, blockInputs, blockGroupInputs }
  } catch {
    return null
  }
}
